import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Store } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../../components/common/Breadcrumb';
import ListingCard from '../components/ListingCard';
import marketplaceAPI from '../services/marketplaceService';
import toast from 'react-hot-toast';

export default function SavedListingsPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();

  const [savedListings, setSavedListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    loadSavedListings();
  }, [user, navigate]);

  const loadSavedListings = async () => {
    setLoading(true);
    try {
      const data = await marketplaceAPI.getSavedListings();
      setSavedListings(data);
    } catch (error) {
      console.error('Failed to load saved listings:', error);
      toast.error('Failed to load saved listings');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsave = async (listingId) => {
    try {
      await marketplaceAPI.unsaveListing(listingId);
      setSavedListings((prev) => prev.filter((s) => s.listing?.id !== listingId));
      toast.success('Removed from saved');
    } catch (error) {
      toast.error('Failed to remove from saved');
    }
  };

  // Transform saved listings to match ListingCard props
  const listings = savedListings
    .filter((s) => s.listing)
    .map((s) => ({
      ...s.listing,
      savedAt: s.savedAt,
    }));

  return (
    <div className={`min-h-screen ${theme.background}`}>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <div className="mb-4">
          <Breadcrumb
            items={[
              { label: 'Home', path: '/' },
              { label: 'Marketplace', path: '/marketplace' },
              { label: 'Saved' }
            ]}
          />
        </div>

        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-2xl font-bold ${theme.text}`}>Saved Listings</h1>
        </div>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className={`rounded-lg ${theme.card} animate-pulse`}
              >
                <div className="h-48 ${theme.backgroundSecondary} rounded-t-lg" />
                <div className="p-4 space-y-3">
                  <div className="h-4 ${theme.backgroundSecondary} rounded w-3/4" />
                  <div className="h-6 ${theme.backgroundSecondary} rounded w-1/2" />
                  <div className="h-4 ${theme.backgroundSecondary} rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className={`text-center py-16 ${theme.card} rounded-lg`}>
            <Heart className={`w-16 h-16 mx-auto mb-4 ${theme.textSecondary}`} />
            <h3 className={`text-lg font-medium mb-2 ${theme.text}`}>
              No saved listings
            </h3>
            <p className={`mb-4 ${theme.textSecondary}`}>
              When you find gear you love, save it here!
            </p>
            <Link
              to="/marketplace"
              className={`inline-flex items-center gap-2 px-6 py-2 ${theme.primary} rounded-lg ${theme.primaryHover}`}
            >
              <Store className="w-5 h-5" />
              Browse Marketplace
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {listings.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                isSaved={true}
                onSave={(id, save) => {
                  if (!save) handleUnsave(id);
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
